import type { TableRow } from '../types/Table.type'
import { WebAwesomeComponent, tryLoadDependencies } from '../../Global/loadDependency'
import type { PaginationProps } from '../../Pagination/types/Pagination.type'

export interface CheckboxElement extends Element {
  checked: boolean
}

export interface CheckboxHandlers {
  onRowCheckboxChange: (rowKey: string, isChecked: boolean) => void
  onSelectAllChange: (isChecked: boolean) => void
}

/**
 * Creates a checkbox element for table header with select-all functionality
 */
export function createSelectAllCheckbox(
  onSelectAllChange: (isChecked: boolean) => void,
  allVisibleRowKeysSelected: boolean,
): HTMLElement {
  const checkbox = document.createElement('wa-checkbox')
  checkbox.className = 'select-all-checkbox'

  if (allVisibleRowKeysSelected) {
    checkbox.setAttribute('checked', '')
  }

  checkbox.addEventListener('change', (e: Event) => {
    const isChecked = (e.target as CheckboxElement).checked
    onSelectAllChange(isChecked)
  })

  return checkbox
}

/**
 * Creates a checkbox element for table row with row selection functionality
 */
export function createRowCheckbox(
  rowKey: string,
  isSelected: boolean,
  onRowCheckboxChange: (rowKey: string, isChecked: boolean) => void,
): HTMLElement {
  const checkbox = document.createElement('wa-checkbox')
  checkbox.className = 'select-row-checkbox'

  if (isSelected) {
    checkbox.setAttribute('checked', '')
  }

  checkbox.addEventListener('change', (e: Event) => {
    const isChecked = (e.target as CheckboxElement).checked
    onRowCheckboxChange(rowKey, isChecked)
  })

  return checkbox
}

/**
 * Creates the table header cell for the select column
 */
export function createSelectColumnHeader(
  enableSelectColumnHeader: boolean,
  onSelectAllChange: (isChecked: boolean) => void,
  allVisibleRowKeysSelected: boolean,
  document: Document,
): HTMLElement {
  const checkboxHeaderTh = document.createElement('th')
  checkboxHeaderTh.setAttribute('part', 'header')
  checkboxHeaderTh.className = 'select-column-header'

  if (enableSelectColumnHeader) {
    tryLoadDependencies(document, [WebAwesomeComponent.Checkbox])
    const checkbox = createSelectAllCheckbox(onSelectAllChange, allVisibleRowKeysSelected)
    checkboxHeaderTh.appendChild(checkbox)
  }

  return checkboxHeaderTh
}

/**
 * Creates the table cell for the select column in a row
 */
export function createSelectColumnCell(
  rowKey: string,
  isSelected: boolean,
  onRowCheckboxChange: (rowKey: string, isChecked: boolean) => void,
  document: Document,
): HTMLElement {
  const checkboxTd = document.createElement('td')
  checkboxTd.setAttribute('part', 'cell')
  checkboxTd.className = 'select-column-cell'

  const checkbox = createRowCheckbox(rowKey, isSelected, onRowCheckboxChange)
  checkboxTd.appendChild(checkbox)

  return checkboxTd
}

/**
 * Gets all visible row keys based on current pagination
 */
export function getVisibleRowKeys(
  dataRows: TableRow[],
  pageInfo: PaginationProps | null,
): string[] {
  let dataToDisplay = dataRows
  if (pageInfo != null) {
    const start = (pageInfo.currentPage - 1) * pageInfo.pageSize
    const end = start + pageInfo.pageSize
    dataToDisplay = dataToDisplay.slice(start, end)
  }

  return dataToDisplay.filter((row) => row.key).map((row) => row.key as string)
}

/**
 * Determines if all visible rows are selected
 */
export function areAllVisibleRowsSelected(
  selectedRowKeys: Set<string>,
  visibleRowKeys: string[],
): boolean {
  return visibleRowKeys.length > 0 && visibleRowKeys.every((key) => selectedRowKeys.has(key))
}

/**
 * Updates the state of a row checkbox in the DOM
 */
export function updateRowCheckboxInDOM(
  shadowRoot: ShadowRoot | null,
  rowIndex: number,
  isSelected: boolean,
): void {
  if (!shadowRoot) return

  const rowCheckboxes = shadowRoot.querySelectorAll(
    'tbody .select-row-checkbox',
  ) as NodeListOf<CheckboxElement>

  if (rowCheckboxes && rowIndex < rowCheckboxes.length) {
    const checkbox = rowCheckboxes[rowIndex]
    if (isSelected) {
      checkbox.setAttribute('checked', '')
    } else {
      checkbox.removeAttribute('checked')
    }
  }
}

/**
 * Updates the state of the select-all checkbox in the DOM
 */
export function updateSelectAllCheckboxInDOM(
  shadowRoot: ShadowRoot | null,
  allSelected: boolean,
): void {
  if (!shadowRoot) return

  const selectAllCheckbox = shadowRoot.querySelector(
    'thead .select-all-checkbox',
  ) as CheckboxElement | null

  if (selectAllCheckbox) {
    if (allSelected) {
      selectAllCheckbox.setAttribute('checked', '')
    } else {
      selectAllCheckbox.removeAttribute('checked')
    }
  }
}

/**
 * Updates all row checkboxes in the DOM based on selectedRowKeys
 */
export function updateAllRowCheckboxesInDOM(
  shadowRoot: ShadowRoot | null,
  visibleRowKeys: string[],
  selectedRowKeys: Set<string>,
): void {
  if (!shadowRoot) return

  const rowCheckboxes = shadowRoot.querySelectorAll(
    'tbody .select-row-checkbox',
  ) as NodeListOf<CheckboxElement>

  if (rowCheckboxes && rowCheckboxes.length > 0) {
    rowCheckboxes.forEach((checkbox, index) => {
      if (index < visibleRowKeys.length) {
        const rowKey = visibleRowKeys[index]
        if (selectedRowKeys.has(rowKey)) {
          checkbox.setAttribute('checked', '')
        } else {
          checkbox.removeAttribute('checked')
        }
      }
    })
  }

  const allSelected = areAllVisibleRowsSelected(selectedRowKeys, visibleRowKeys)
  updateSelectAllCheckboxInDOM(shadowRoot, allSelected)
}
